import { VoiceProduct } from '@shared/models/voice-product.model';
import {
  AdditionalServiceSet,
  AdditionalServiceVoice,
  ServiceStatus,
} from '../modules/subscription/models/additional-service-voice.model';
import { MbbProduct } from '../modules/mbb/models/mbb-product.model';
import { AdditionalService } from '../models/order-capture.model';
import { Injectable } from '@angular/core';
import { Subscription, SubscriptionStatus } from '@app/shared/models/subscription-model';
import { ProductHelperService } from './product-helper.service';
import { AssignedSubscriptionModel, ProductDate } from '@app/shared/models/assigned-subscription-offering.model';
import { VoiceSubscriptionType } from '../modules/subscription/models/offerings-response.model';
import { SimCardDelivery } from '../models/simcard.model';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionHelperService {
  static getChangedVoiceSubscriptions(existingCustomerProducts: VoiceProduct[]) {
    return existingCustomerProducts
      .filter(s => s.isExistingSubscription)
      .filter(
        s =>
          (s.offering && s.offering.id) ||
          (s.existingSubscriber && VoiceProduct.hasSubscriberChanged(s)) ||
          (s.subscriptionServices && this.getChangedServicesOnExistingVoiceProduct(s).length > 0) ||
          s.replaceVoiceSimIcc ||
          s.replaceVoiceESim ||
          s.replaceDataSimIcc ||
          s.replaceTwinSimIcc
      );
  }

  static isAditionalServiceActiveOnExistingVoiceProduct(product: VoiceProduct, additionalService: AdditionalServiceVoice) {
    if (additionalService.mutexSetName) {
      return !!product.subscriptionServices?.mutexServiceSets.find(
        set =>
          set.name === additionalService.mutexSetName &&
          set.mutexServices.find(
            service => service.productId === additionalService.productId && this.isServiceActive(service)
          )
      );
    }
    return !!product.subscriptionServices?.serviceProducts.find(
      service => service.productId === additionalService.productId && this.isServiceActive(service)
    );
  }

  static getChangedServicesOnExistingVoiceProduct(voiceProduct: VoiceProduct): AdditionalService[] {
    var addedServices = SubscriptionHelperService.getServicesAddedToExistingVoiceProduct(voiceProduct);
    var removedServices = SubscriptionHelperService.getServicesRemovedFromExistingVoiceProduct(voiceProduct);
    return [
      ...addedServices.map(
        s =>
          ({
            productId: s.productId,
            description: s.description,
            isServiceAdded: true,
            icc: s.icc,
            isCampaign: s.isCampaign,
            monthlyPrice: s.price,
            isBarringService: s.isBarringService,
          }) as AdditionalService
      ),
      ...removedServices.map(
        s =>
          ({
            productId: s.productId,
            description: s.description,
            isServiceAdded: false,
            shouldBeImplicitlyRemoved: !this.isServiceAvailableForChangedSubscription(s, voiceProduct.subscriptionServices),
            icc: s.icc,
            isCampaign: s.isCampaign,
            monthlyPrice: s.price,
            isBarringService: s.isBarringService,
          }) as AdditionalService
      ),
    ];
  }

  static getServicesAddedToExistingVoiceProduct(product: VoiceProduct): AdditionalServiceVoice[] {
    if (!product.originalSubscriptionServices) return [];
    const existingActiveServicesOnSubscription = this.getExistingActiveAdditionalServicesOnSubscription(
      product.originalSubscriptionServices
    );
    const addedServices: AdditionalServiceVoice[] = [];

    product.additionalServices.forEach(service => {
      const serviceAlreadyActive = existingActiveServicesOnSubscription.find(existing =>
        this.isSameInstance(existing, service)
      );
      if (!serviceAlreadyActive) {
        addedServices.push(service);
      }
    });

    return addedServices;
  }

  static getServicesRemovedFromExistingVoiceProduct(product: VoiceProduct): AdditionalServiceVoice[] {
    if (!product.originalSubscriptionServices) return [];
    const existingActiveServicesOnSubscription = this.getExistingActiveAdditionalServicesOnSubscription(
      product.originalSubscriptionServices
    );
    const removedServices: AdditionalServiceVoice[] = [];

    existingActiveServicesOnSubscription.forEach(existing => {
      const isServiceRemoved = !product.additionalServices.find(service => this.isSameInstance(service, existing));
      if (isServiceRemoved) {
        removedServices.push(existing);
      }
    });

    return removedServices;
  }

  static isSubscriptionFrozenOrSuspended(subscription: Subscription): boolean {
    return (
      subscription.status === SubscriptionStatus.Frozen ||
      subscription.status === SubscriptionStatus.Suspended ||
      subscription.status === SubscriptionStatus.LostStolenFrozen
    );
  }

  static isServiceAvailableForChangedSubscription(
    existingService: AdditionalServiceVoice,
    newSubscriptionServices: AdditionalServiceSet
  ): boolean {
    if (existingService.mutexSetName) {
      return newSubscriptionServices.mutexServiceSets
        .find(mutexServiceSet => mutexServiceSet.name === existingService.mutexSetName)
        ?.mutexServices.some(mutexService => mutexService.productId === existingService.productId);
    }

    return newSubscriptionServices.serviceProducts.some(service => service.productId === existingService.productId);
  }

  static getContinuedServicesOnUpgradedVoiceProduct(product: VoiceProduct): AdditionalServiceVoice[] {
    const continuedServices: AdditionalServiceVoice[] = [];
    if (product.offering?.id) {
      if (!product.originalSubscriptionServices) return [];
      const existingActiveServicesOnSubscription = this.getExistingActiveAdditionalServicesOnSubscription(
        product.originalSubscriptionServices
      );

      existingActiveServicesOnSubscription.forEach(existing => {
        const isServiceStillActive = product.additionalServices.find(service => this.isSameInstance(service, existing));
        if (isServiceStillActive) {
          continuedServices.push(existing);
        }
      });
    }
    return continuedServices;
  }

  static getExistingActiveAdditionalServicesOnSubscription(
    subscriptionServices: AdditionalServiceSet
  ): AdditionalServiceVoice[] {
    if (!subscriptionServices) {
      return [];
    }

    const activeAdditionalServices: AdditionalServiceVoice[] = subscriptionServices.serviceProducts.filter(service =>
      SubscriptionHelperService.isServiceActive(service)
    );

    const activeMutexServices = subscriptionServices.mutexServiceSets
      .filter(set => set.mutexServices.some(service => SubscriptionHelperService.isServiceActive(service)))
      .map(service => {
        const selectedService = service.mutexServices.find(s => SubscriptionHelperService.isServiceActive(s));
        return { ...selectedService, mutexSetName: service.name };
      });

    return activeAdditionalServices.concat(activeMutexServices);
  }

  static isSameInstance(a: AdditionalServiceVoice, b: AdditionalServiceVoice): boolean {
    return a.instanceId && b.instanceId ? a.instanceId === b.instanceId : a.productId === b.productId;
  }

  static isAdditionalSim(service: AdditionalServiceVoice): boolean {
    return !!(service?.isDataSimCard || service?.isTwinSimCard);
  }

  static withActiveSimInstanceIds(serviceProducts: AdditionalServiceVoice[]): AdditionalServiceVoice[] {
    return serviceProducts.map((service, index) =>
      this.isAdditionalSim(service) && this.isServiceActive(service)
        ? { ...service, instanceId: `existing-${service.icc || service.aNumber || index}` }
        : service
    );
  }

  static isServiceActive(service: AdditionalServiceVoice): boolean {
    return service.serviceStatus === ServiceStatus.Active || service.serviceStatus === ServiceStatus.Assigning;
  }

  static isServiceSuspended(service: AdditionalServiceVoice): boolean {
    return service.serviceStatus === ServiceStatus.ActiveUntilEndDate;
  }

  static getActiveServices(subscriptionServices: AdditionalServiceSet) {
    let activeSubscriptionServices: AdditionalServiceVoice[] = [];

    if (!subscriptionServices) {
      return [];
    }
    activeSubscriptionServices = subscriptionServices.serviceProducts.filter(
      service => service.isCampaign || this.isServiceActive(service)
    );
    subscriptionServices.mutexServiceSets.forEach(set => {
      activeSubscriptionServices = activeSubscriptionServices.concat(
        set.mutexServices.filter(service => this.isServiceActive(service))
      );
    });

    return activeSubscriptionServices;
  }

  static getSuspendedServices(subscriptionServices: AdditionalServiceSet) {
    let activeSubscriptionServices: AdditionalServiceVoice[] = [];

    if (!subscriptionServices) {
      return [];
    }
    activeSubscriptionServices = subscriptionServices.serviceProducts.filter(service => this.isServiceSuspended(service));
    subscriptionServices.mutexServiceSets.forEach(set => {
      activeSubscriptionServices = activeSubscriptionServices.concat(
        set.mutexServices.filter(service => this.isServiceSuspended(service))
      );
    });

    return activeSubscriptionServices;
  }

  static getDateFromProductDate(productDate: ProductDate): Date | null {
    return new Date(productDate?.year, productDate?.month - 1, productDate?.day);
  }

  static getFutureBindingExpirationDate(product: VoiceProduct | MbbProduct): Date | null {
    const sub = ProductHelperService.isMbbProduct(product) ? product.existingSubscription : product.subscription;
    const expirationDate = sub?.commitmentPeriods?.[0]?.expirationDate;

    return new Date(expirationDate) > new Date() ? expirationDate : null;
  }

  static subscriptionBindingExpirationDate(assignedSubscription: AssignedSubscriptionModel): Date | null {
    if (!assignedSubscription || assignedSubscription?.bindings?.length === 0) {
      return null;
    }
    const expirationDate = SubscriptionHelperService.getDateFromProductDate(assignedSubscription.bindings[0].expirationDate);
    return new Date(expirationDate) > new Date() ? expirationDate : null;
  }

  /**
   * Rebuilds the seller's delivery choice from the boolean `orderSim` contract that is persisted
   * on the product. An entered ICC can only have come from handing the card over in store.
   */
  static getSimCardDelivery(icc: string, orderSim: boolean): SimCardDelivery | null {
    if (orderSim) {
      return SimCardDelivery.ByMail;
    }
    return icc ? SimCardDelivery.InStore : null;
  }

  static isProductIceJunior(subscriptionType: string): boolean {
    // In CDs subscriptionType is Freemium, while in proffs it is iceJunior
    return subscriptionType === VoiceSubscriptionType.Freemium || subscriptionType === VoiceSubscriptionType.IceJunior;
  }

  static isProductIceJuniorPremium(subscriptionType: string): boolean {
    return subscriptionType === VoiceSubscriptionType.IceJuniorPremium;
  }
}
